// Extracted from LocationFilter/index.tsx (S1 follow-up row `:77` — 3+
// co-located hooks cross this repo's "extract to co-located useXxx.ts"
// convention).
import type { InboxLocation } from 'api/feedbackInbox.types'
import { buildTranslate } from 'locales'
import { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { generateId } from 'utilities/uniqueId'

const t = buildTranslate('feedback_inbox.filter_bar.location')

export interface UseLocationFilterResult {
  search: string
  setSearch: (value: string) => void
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  selectedIdSet: Set<number>
  filteredLocations: InboxLocation[]
  toggleLocation: (id: number) => void
  selectAllLocations: () => void
  // Sketch v83 `#mLocD.rai-on`: true only when RecoveryAI is on at some
  // locations but not all. Gates the quick-selects, count and badges.
  showRaiStatus: boolean
  raiOnCount: number
  selectRaiOn: () => void
  selectRaiOff: () => void
  triggerAriaLabel: string
  searchInputRef: (node: HTMLInputElement | null) => void
  triggerId: string
  panelRef: RefObject<HTMLDivElement>
  handleClose: () => void
}

export function useLocationFilter(
  locations: InboxLocation[],
  selectedIds: number[],
  onChange: (ids: number[]) => void
): UseLocationFilterResult {
  const [search, setSearch] = useState('')
  // `Popover` here is baseui's `StatefulPopover` (see @thanx/uikit's
  // `Popover` wrapper) — it manages its own open/closed state internally and
  // exposes no `isOpen` prop, only `onOpen`/`onClose` callbacks. Mirroring
  // that state locally is the least invasive way to drive `aria-expanded` on
  // the trigger.
  const [isOpen, setIsOpen] = useState(false)

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  // A location can have no name (the API layer turns null into ''). Label it
  // so its checkbox has an accessible name, and so the search can match it.
  const labeledLocations = useMemo(
    () =>
      locations.map(location =>
        location.name.trim()
          ? location
          : { ...location, name: t('unnamed_location') }
      ),
    [locations]
  )

  const filteredLocations = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return labeledLocations
    return labeledLocations.filter(location =>
      location.name.toLowerCase().includes(query)
    )
  }, [labeledLocations, search])

  // Wrapped in useCallback per this repo's hook convention (a fresh closure
  // on every call is a foot-gun for a future consumer that puts it in an
  // effect's dependency array — see reference_redux_orm_effect_dep_object_
  // identity_loop for the class of bug an unstable ref causes).
  const toggleLocation = useCallback(
    (id: number) => {
      const next = selectedIdSet.has(id)
        ? selectedIds.filter(existing => existing !== id)
        : [...selectedIds, id]
      onChange(next)
    },
    [selectedIdSet, selectedIds, onChange]
  )

  // `selectedIds: []` means "all locations" (see LocationFilterProps) — so
  // clearing the selection IS selecting "All locations", not a third state.
  const selectAllLocations = useCallback(() => onChange([]), [onChange])

  // A null status (unknown) is in neither list. The quick-selects REPLACE the
  // selection. Both lists are non-empty whenever they show, so neither can
  // emit [], which would widen the filter to every location.
  const { raiOnIds, raiOffIds } = useMemo(
    () => ({
      raiOnIds: locations
        .filter(location => location.recoveryAiEnabled === true)
        .map(location => location.id),
      raiOffIds: locations
        .filter(location => location.recoveryAiEnabled === false)
        .map(location => location.id),
    }),
    [locations]
  )
  const showRaiStatus = raiOnIds.length > 0 && raiOffIds.length > 0
  const selectRaiOn = useCallback(
    () => onChange(raiOnIds),
    [onChange, raiOnIds]
  )
  const selectRaiOff = useCallback(
    () => onChange(raiOffIds),
    [onChange, raiOffIds]
  )

  // The dropdown-style trigger (v83 prototype `.f-dd2`) always shows the
  // plain "Location" label visually — the count lives in the belize pill
  // instead (`CountBadge`, tone="primary"). The accessible name still needs
  // to carry the count for screen-reader users, since the pill glyph itself
  // is `aria-hidden` (see `CountBadge`) — `triggerAriaLabel` is what the
  // Button's own `aria-label` uses, which pre-empts the pill's `srLabel`
  // from ever being reached (same pattern as the kind-segment count pills in
  // `InboxFilterBar`).
  const triggerAriaLabel =
    selectedIds.length === 0
      ? t('trigger')
      : t('trigger_with_count', { count: selectedIds.length })

  // Callback ref for the search input. The popover content mounts each time
  // the popover opens, and baseui's popover leaves focus where it was (it
  // only auto-focuses with `focusLock`). So typing straight after a click
  // reached the trigger or <body>, not this field. The ref must keep one
  // identity: React re-runs a changed callback ref on every render, which
  // would pull focus back here after each checkbox toggle. `preventScroll`:
  // on mount the panel still sits at the page's top-left (popper hasn't
  // positioned it yet), so a plain focus() scrolls the page up.
  const searchInputRef = useCallback((node: HTMLInputElement | null) => {
    node?.focus({ preventScroll: true })
  }, [])

  // baseui's `returnFocus` only acts with `focusLock` on, so closing never
  // returned focus. Now that focus moves into the panel, Escape would drop it
  // to <body> when the search field unmounts. Restore it to the trigger only
  // when it was lost (on <body> or still inside the panel): an outside click
  // onto another control keeps that control focused, since the browser moves
  // focus after this runs. `preventScroll` so that focus can't scroll the
  // page mid-click. The trigger is found by id because baseui replaces the
  // anchor's `ref` with its own. The search is cleared so typing after a
  // reopen starts a new query (same as LocationMultiSelect).
  const [triggerId] = useState(() => `location-filter-trigger-${generateId()}`)
  const panelRef = useRef<HTMLDivElement>(null)
  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSearch('')
    const active = document.activeElement
    const focusLost =
      !active ||
      active === document.body ||
      !!panelRef.current?.contains(active)
    if (focusLost) {
      document.getElementById(triggerId)?.focus({ preventScroll: true })
    }
  }, [triggerId])

  return {
    search,
    setSearch,
    isOpen,
    setIsOpen,
    selectedIdSet,
    filteredLocations,
    toggleLocation,
    selectAllLocations,
    showRaiStatus,
    raiOnCount: raiOnIds.length,
    selectRaiOn,
    selectRaiOff,
    triggerAriaLabel,
    searchInputRef,
    triggerId,
    panelRef,
    handleClose,
  }
}
